import { useState } from 'react'
import { Bookmark, Loader2, Play, Save, Trash2, X } from 'lucide-react'
import clsx from 'clsx'
import type { DealSearchSchema, SavedSearch } from './types'

/** Saved report definitions.
 *
 *  A saved search stores filters + columns, never results — so "run" answers
 *  today's question, and "run for…" answers the same question for a different
 *  period. That distinction is the requirement, so it is made explicit in the
 *  UI rather than hidden behind a single button.
 */
export function SavedSearchPanel({
  views,
  schema,
  loading,
  busyId,
  activeId,
  canSave,
  onRun,
  onSave,
  onDelete,
  onClose,
}: {
  views: SavedSearch[]
  schema: DealSearchSchema | undefined
  loading: boolean
  busyId: string | null
  activeId: string | null
  canSave: boolean
  onRun: (view: SavedSearch, datePreset: string | null) => void
  onSave: (name: string, description: string) => Promise<void>
  onDelete: (view: SavedSearch) => void
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [rerunPreset, setRerunPreset] = useState<Record<string, string>>({})

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim() || saving) return
    setSaving(true)
    try {
      await onSave(name.trim(), description.trim())
      setName('')
      setDescription('')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Saved reports"
      className="absolute right-0 top-full z-30 mt-1 w-[440px] rounded-lg border border-amber-300/15 bg-[#0d0d0d] shadow-2xl shadow-black/60"
    >
      <div className="flex items-center justify-between border-b border-amber-300/10 px-3 py-2">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
          <Bookmark size={11} /> Saved reports
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close saved reports"
          className="rounded p-1 text-stone-500 transition hover:text-amber-200"
        >
          <X size={12} />
        </button>
      </div>

      <div className="max-h-[300px] overflow-y-auto">
        {loading ? (
          <p className="px-3 py-4 text-center text-[11px] text-stone-500">Loading…</p>
        ) : views.length === 0 ? (
          <p className="px-3 py-4 text-center text-[11px] text-stone-600">
            No saved reports yet. Build a search below and save it — the definition is stored, so
            it can be re-run against any period.
          </p>
        ) : (
          <ul className="divide-y divide-amber-300/[0.06]">
            {views.map((view) => (
              <li
                key={view.id}
                className={clsx('px-3 py-2', activeId === view.id && 'bg-amber-400/[0.05]')}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-medium text-stone-100">
                      {view.name}
                    </div>
                    {view.description && (
                      <div className="truncate text-[10px] text-stone-500">{view.description}</div>
                    )}
                    <div className="mt-0.5 text-[9px] text-stone-600">
                      {view.columns.length} columns · {view.ownerEmail}
                      {view.lastRunAt ? ` · last run ${view.lastRunAt.slice(0, 10)}` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(view)}
                    aria-label={`Delete ${view.name}`}
                    className="rounded p-1 text-stone-600 transition hover:text-red-400"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>

                <div className="mt-1.5 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onRun(view, null)}
                    disabled={busyId === view.id}
                    className="flex items-center gap-1 rounded border border-amber-300/20 bg-amber-400/[0.08] px-2 py-1 text-[10px] text-amber-100 transition hover:bg-amber-400/[0.14] disabled:opacity-50"
                  >
                    {busyId === view.id ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <Play size={10} />
                    )}
                    Run as saved
                  </button>
                  <select
                    value={rerunPreset[view.id] ?? ''}
                    onChange={(event) => {
                      const preset = event.target.value
                      setRerunPreset((prev) => ({ ...prev, [view.id]: preset }))
                      if (preset) onRun(view, preset)
                    }}
                    aria-label={`Re-run ${view.name} for a different period`}
                    className="flex-1 rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-300 focus:border-amber-300/30 focus:outline-none"
                  >
                    <option value="">Re-run for…</option>
                    {(schema?.datePresets ?? []).map((preset) => (
                      <option key={preset.key} value={preset.key}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={submit} className="border-t border-amber-300/10 p-3">
        <div className="mb-1.5 text-[9px] uppercase tracking-[0.16em] text-stone-600">
          Save the current search
        </div>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Report name"
          aria-label="Report name"
          className="mb-1.5 w-full rounded border border-amber-300/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
        />
        <input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="What this report answers (optional)"
          aria-label="Report description"
          className="mb-1.5 w-full rounded border border-amber-300/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!name.trim() || saving || !canSave}
          className="flex w-full items-center justify-center gap-1.5 rounded border border-amber-300/25 bg-amber-400/[0.1] px-2 py-1.5 text-[11px] font-medium text-amber-100 transition hover:bg-amber-400/[0.16] disabled:opacity-40"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          Save definition
        </button>
      </form>
    </div>
  )
}
